import { Sparkles, Clock, AlertTriangle, CircleDashed } from "lucide-react";
import { cn } from "@/lib/utils";
import type { EnrichmentStatus } from "@/types/api";

interface EnrichmentIndicatorProps {
  status: EnrichmentStatus | null;
  className?: string;
}

/**
 * Compact, status-only AI-enrichment indicator for alert list rows (#545).
 *
 * Mirrors the visual language of the alert detail panel's AI Analysis section
 * (Sparkles = ready, Clock = pending, AlertTriangle = failed, CircleDashed =
 * no analysis). Only an explicit `"pending"` renders as active pending
 * analysis; a `null`/absent status means enrichment is unavailable or has not
 * started, so it renders the neutral "no analysis" state rather than
 * overclaiming queued work — matching the detail panel.
 *
 * Renders only the enrichment STATE — never raw or untrusted AI text — so it is
 * safe to show in dense list rows. The wrapper is `role="img"` with a full
 * `aria-label`, so screen readers announce the complete meaning while sighted
 * users see a compact label.
 */
const CONFIG = {
  complete: {
    label: "AI ready",
    description: "AI analysis ready",
    Icon: Sparkles,
    tone: "text-green-400",
  },
  failed: {
    label: "Review",
    description: "AI analysis failed — manual review required",
    Icon: AlertTriangle,
    tone: "text-destructive",
  },
  pending: {
    label: "Pending",
    description: "AI analysis pending",
    Icon: Clock,
    tone: "text-muted-foreground",
  },
  none: {
    label: "No AI",
    description: "No AI analysis — unavailable or not started",
    Icon: CircleDashed,
    tone: "text-muted-foreground",
  },
} as const;

export function EnrichmentIndicator({ status, className }: EnrichmentIndicatorProps) {
  const key =
    status === "complete" || status === "failed" || status === "pending"
      ? status
      : "none";
  const { label, description, Icon, tone } = CONFIG[key];

  return (
    <span
      role="img"
      aria-label={description}
      title={description}
      data-testid={`enrichment-indicator-${key}`}
      className={cn(
        "inline-flex items-center gap-1 text-xs font-medium",
        tone,
        className,
      )}
    >
      <Icon aria-hidden className="h-3.5 w-3.5 shrink-0" />
      <span className="hidden md:inline">{label}</span>
    </span>
  );
}
